import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    collection, onSnapshot, query, orderBy, doc, addDoc, updateDoc, deleteDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { AY_ADLARI, paraCevir, ayKey } from '../utils/takvim';
import {
    ArrowLeft, Wallet, TrendingUp, AlertCircle,
    Plus, X, Receipt, ChevronDown, ChevronUp, HardHat, CheckCircle2,
    CreditCard, Trash2, Edit3, Check
} from 'lucide-react';

export default function UstaHesap() {
    const { ustaId } = useParams();
    const navigate = useNavigate();
    const bugun = new Date();

    const [odenmisAylariGoster, setOdenmisAylariGoster] = useState(false);
    const [usta, setUsta] = useState(null);
    const [calismaVerisi, setCalismaVerisi] = useState({});
    const [avansVerisi, setAvansVerisi] = useState({});
    const [odemeler, setOdemeler] = useState([]);
    const [yukleniyor, setYukleniyor] = useState(true);

    // Modal states
    const [odemeModalAcik, setOdemeModalAcik] = useState(false);
    const [odemeAy, setOdemeAy] = useState(null);
    const [odemeTutari, setOdemeTutari] = useState('');
    const [odemeTarihi, setOdemeTarihi] = useState(new Date().toISOString().split('T')[0]);
    const [odemeAciklama, setOdemeAciklama] = useState('');
    const [duzenlemeModu, setDuzenlemeModu] = useState(false);
    const [seciliOdeme, setSeciliOdeme] = useState(null);

    // UI states
    const [acikAylar, setAcikAylar] = useState(new Set());
    const [odemeGecmisiAcik, setOdemeGecmisiAcik] = useState(false);

    // Verileri çek
    useEffect(() => {
        const unsubUsta = onSnapshot(doc(db, 'ustalar', ustaId), snap => {
            if (snap.exists()) setUsta({ id: snap.id, ...snap.data() });
            else navigate('/muhasebe');
        });

        const aylar = [];
        for (let i = -10; i <= 2; i++) {
            const d = new Date(bugun.getFullYear(), bugun.getMonth() + i, 1);
            aylar.push(ayKey(d.getFullYear(), d.getMonth()));
        }

        const unsubs = [];
        aylar.forEach(ak => {
            const unsubCalisma = onSnapshot(doc(db, 'aylikCalisma', ak), snap => {
                setCalismaVerisi(prev => ({ ...prev, [ak]: snap.exists() ? snap.data().kayitlar : {} }));
            });
            const unsubAvans = onSnapshot(doc(db, 'aylikAvans', ak), snap => {
                setAvansVerisi(prev => ({ ...prev, [ak]: snap.exists() ? snap.data() : {} }));
            });
            unsubs.push(unsubCalisma, unsubAvans);
        });

        const unsubOdemeler = onSnapshot(
            query(collection(db, 'odemeler'), orderBy('tarih', 'desc')),
            snap => setOdemeler(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => o.ustaId === ustaId))
        );

        setYukleniyor(false);

        return () => {
            unsubUsta();
            unsubs.forEach(u => u());
            unsubOdemeler();
        };
    }, [ustaId, navigate]);

    // AYLIK ÖZET HESAPLA - Ödemeler ilgiliAy'e göre dağıtılır
    const aylikOzet = useMemo(() => {
        if (!usta) return {};

        const ozet = {};

        // 1. ÇALIŞMA (Hakediş)
        Object.entries(calismaVerisi).forEach(([ak, kayitlar]) => {
            if (!kayitlar) return;
            let gunSayisi = 0;
            Object.values(kayitlar).forEach(ustaMap => {
                if (ustaMap && ustaMap[ustaId]) gunSayisi++;
            });
            if (gunSayisi > 0) {
                const hakedis = gunSayisi * Number(usta.yovmiye || 0);
                if (!ozet[ak]) ozet[ak] = { gunSayisi: 0, hakedis: 0, avans: 0, odeme: 0, odemeKayitlari: [] };
                ozet[ak].gunSayisi += gunSayisi;
                ozet[ak].hakedis += hakedis;
            }
        });

        // 2. AVANS
        Object.entries(avansVerisi).forEach(([ak, ustaMap]) => {
            const ustaVerisi = ustaMap?.[ustaId];
            if (!ustaVerisi) return;
            if (!ozet[ak]) ozet[ak] = { gunSayisi: 0, hakedis: 0, avans: 0, odeme: 0, odemeKayitlari: [] };

            if (typeof ustaVerisi === 'object') {
                Object.entries(ustaVerisi).forEach(([gunKey, tutar]) => {
                    ozet[ak].avans += Number(tutar || 0);
                });
            } else if (typeof ustaVerisi === 'number') {
                ozet[ak].avans += Number(ustaVerisi || 0);
            }
        });

        // 3. ÖDEMELER - ilgiliAy'e göre dağıt, yoksa tarih ayına dağıt
        odemeler.forEach(o => {
            const odemeTutari = Number(o.tutar || 0);
            // Önce ilgiliAy varsa ona dağıt
            const hedefAy = o.ilgiliAy || (() => {
                const tarih = o.tarih?.toDate ? o.tarih.toDate() : new Date(o.tarih);
                return ayKey(tarih.getFullYear(), tarih.getMonth());
            })();

            if (!ozet[hedefAy]) ozet[hedefAy] = { gunSayisi: 0, hakedis: 0, avans: 0, odeme: 0, odemeKayitlari: [] };
            ozet[hedefAy].odeme += odemeTutari;
            ozet[hedefAy].odemeKayitlari.push(o);
        });

        return ozet;
    }, [usta, calismaVerisi, avansVerisi, odemeler]);

    // TOPLAM HESAPLA
    const { toplamHakedis, toplamAvans, toplamOdeme, netBakiye } = useMemo(() => {
        let th = 0, ta = 0, to = 0;
        Object.values(aylikOzet).forEach(o => {
            th += o.hakedis;
            ta += o.avans;
            to += o.odeme;
        });
        return { toplamHakedis: th, toplamAvans: ta, toplamOdeme: to, netBakiye: th - ta - to };
    }, [aylikOzet]);

    // BORÇLU AYLAR (hakedis > avans + odeme)
    const borcluAylar = useMemo(() => {
        return Object.entries(aylikOzet)
            .filter(([_, o]) => o.hakedis > o.avans + o.odeme)
            .sort((a, b) => a[0].localeCompare(b[0]));
    }, [aylikOzet]);

    // ÖDENMİŞ AYLAR
    const odenmisAylar = useMemo(() => {
        return Object.entries(aylikOzet)
            .filter(([_, o]) => o.hakedis > 0 && o.hakedis <= o.avans + o.odeme)
            .sort((a, b) => a[0].localeCompare(b[0]));
    }, [aylikOzet]);

    // TÜM AYLAR
    const tumAylar = useMemo(() => {
        return Object.entries(aylikOzet).sort((a, b) => a[0].localeCompare(b[0]));
    }, [aylikOzet]);

    const toggleAy = (ak) => {
        setAcikAylar(prev => {
            const yeni = new Set(prev);
            if (yeni.has(ak)) yeni.delete(ak);
            else yeni.add(ak);
            return yeni;
        });
    };

    // ÖDEME KAYDET / GÜNCELLE
    const odemeKaydet = async () => {
        if (!odemeTutari || !odemeAy) return;

        try {
            const tutar = Number(odemeTutari);

            if (duzenlemeModu && seciliOdeme) {
                // GÜNCELLE
                await updateDoc(doc(db, 'odemeler', seciliOdeme.id), {
                    tutar,
                    tarih: new Date(odemeTarihi),
                    aciklama: odemeAciklama || `${odemeAy} Ayı Düzenleme`,
                    ilgiliAy: odemeAy,
                    guncellemeTarihi: serverTimestamp()
                });
            } else {
                // YENİ KAYDET
                await addDoc(collection(db, 'odemeler'), {
                    ustaId,
                    tarih: new Date(odemeTarihi),
                    tutar,
                    aciklama: odemeAciklama || `${odemeAy} Ayı Hesap Kapatma`,
                    kesimNoktasi: true,
                    ilgiliAy: odemeAy,
                    olusturmaTarihi: serverTimestamp()
                });
            }

            modalKapat();
        } catch (err) {
            alert('Ödeme kaydedilemedi: ' + err.message);
            console.error(err);
        }
    };

    // ÖDEME SİL
    const odemeSil = async (odemeId) => {
        if (!confirm('Bu ödemeyi silmek istediğinize emin misiniz?')) return;
        try {
            await deleteDoc(doc(db, 'odemeler', odemeId));
        } catch (err) {
            alert('Silme işlemi başarısız: ' + err.message);
        }
    };

    // MODAL AÇ
    const odemeModalAc = (ak, mevcutOdeme = null) => {
        const ayVerisi = aylikOzet[ak];
        if (!ayVerisi) return;
        const kalan = ayVerisi.hakedis - ayVerisi.avans - ayVerisi.odeme;

        setOdemeAy(ak);

        if (mevcutOdeme) {
            // DÜZENLEME MODU
            setDuzenlemeModu(true);
            setSeciliOdeme(mevcutOdeme);
            setOdemeTutari(String(mevcutOdeme.tutar));
            const tarih = mevcutOdeme.tarih?.toDate ? mevcutOdeme.tarih.toDate() : new Date(mevcutOdeme.tarih);
            setOdemeTarihi(tarih.toISOString().split('T')[0]);
            setOdemeAciklama(mevcutOdeme.aciklama || '');
        } else {
            // YENİ ÖDEME
            setDuzenlemeModu(false);
            setSeciliOdeme(null);
            setOdemeTutari(String(Math.max(0, Math.ceil(kalan))));
            setOdemeAciklama(`${ak} Ayı Hesap Kapatma`);
        }

        setOdemeModalAcik(true);
    };

    // MODAL KAPAT
    const modalKapat = () => {
        setOdemeModalAcik(false);
        setOdemeAy(null);
        setOdemeTutari('');
        setOdemeAciklama('');
        setDuzenlemeModu(false);
        setSeciliOdeme(null);
    };

    // "ÖDENDİ İŞARETLE" - Sıfır tutarlı ödeme (düzeltme için)
    const odenmisIsaretle = async (ak) => {
        const ayVerisi = aylikOzet[ak];
        if (!ayVerisi) return;
        const kalan = ayVerisi.hakedis - ayVerisi.avans - ayVerisi.odeme;
        if (kalan <= 0) return;

        try {
            await addDoc(collection(db, 'odemeler'), {
                ustaId,
                tarih: new Date(),
                tutar: kalan,
                aciklama: `${ak} Ayı - Manuel Kapatma`,
                kesimNoktasi: true,
                ilgiliAy: ak,
                manuel: true,
                olusturmaTarihi: serverTimestamp()
            });
        } catch (err) {
            alert('İşaretleme başarısız: ' + err.message);
        }
    };

    if (yukleniyor || !usta) {
        return (
            <div className="p-8 max-w-4xl mx-auto flex items-center justify-center min-h-[50vh]">
                <div className="text-stone-600 text-sm font-bold animate-pulse">Yükleniyor...</div>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6 pb-24">
            {/* Geri Butonu & Başlık */}
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/muhasebe')}
                    className="p-3 bg-stone-900 border border-stone-800 rounded-2xl text-stone-400 hover:text-white hover:bg-stone-800 transition-all active:scale-95"
                >
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-black text-white tracking-tight flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-stone-800 border border-stone-700 flex items-center justify-center text-stone-500 font-black text-lg">
                            {usta.ad?.[0]?.toUpperCase()}
                        </div>
                        {usta.ad}
                    </h1>
                    <p className="text-xs text-stone-500 font-bold uppercase tracking-widest mt-1">
                        Yevmiye: {paraCevir(usta.yovmiye)} | Günlük Baz Hakediş
                    </p>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* ÖZET KARTLARI */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {/* BAKİYE - Ana kart */}
                <div className={`border-2 p-5 rounded-3xl shadow-lg relative overflow-hidden ${netBakiye > 0
                        ? 'bg-red-500/5 border-red-500/20'
                        : 'bg-green-500/5 border-green-500/20'
                    }`}>
                    <div className={`absolute top-0 right-0 p-3 opacity-10 ${netBakiye > 0 ? 'text-red-500' : 'text-green-500'}`}>
                        {netBakiye > 0 ? <AlertCircle size={64} /> : <CheckCircle2 size={64} />}
                    </div>
                    <div className={`flex items-center gap-2 mb-3 ${netBakiye > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        <Receipt size={14} />
                        <span className="text-[10px] font-black uppercase tracking-wider">
                            {netBakiye > 0 ? 'ÖDENECEK' : 'BAKİYE'}
                        </span>
                    </div>
                    <div className={`text-3xl font-black tracking-tight ${netBakiye > 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {paraCevir(Math.abs(netBakiye))}
                    </div>
                    <p className={`text-[10px] mt-2 font-bold ${netBakiye > 0 ? 'text-red-400/60' : 'text-green-400/60'}`}>
                        {netBakiye > 0
                            ? `${borcluAylar.length} ay borçlu`
                            : toplamHakedis > 0 ? 'Tüm borçlar ödenmiş ✓' : 'Henüz çalışma yok'
                        }
                    </p>
                </div>

                <div className="bg-stone-900 border border-stone-800 p-5 rounded-3xl shadow-lg">
                    <div className="flex items-center gap-2 mb-3 text-stone-500">
                        <HardHat size={14} />
                        <span className="text-[10px] font-black uppercase tracking-wider">Hakediş</span>
                    </div>
                    <div className="text-xl font-black text-green-400">{paraCevir(toplamHakedis)}</div>
                </div>

                <div className="bg-stone-900 border border-stone-800 p-5 rounded-3xl shadow-lg">
                    <div className="flex items-center gap-2 mb-3 text-stone-500">
                        <Wallet size={14} />
                        <span className="text-[10px] font-black uppercase tracking-wider">Avans</span>
                    </div>
                    <div className="text-xl font-black text-yellow-500">{paraCevir(toplamAvans)}</div>
                </div>

                <div className="bg-stone-900 border border-stone-800 p-5 rounded-3xl shadow-lg">
                    <div className="flex items-center gap-2 mb-3 text-stone-500">
                        <CheckCircle2 size={14} />
                        <span className="text-[10px] font-black uppercase tracking-wider">Ödenen</span>
                    </div>
                    <div className="text-xl font-black text-blue-400">{paraCevir(toplamOdeme)}</div>
                </div>
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* BORÇLU AYLAR */}
            {/* ═══════════════════════════════════════════════════════════ */}
            <div className="bg-stone-900 border border-stone-800 rounded-3xl overflow-hidden shadow-2xl">
                <div className="px-6 py-4 border-b border-stone-800 bg-stone-900/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <AlertCircle size={16} className="text-red-500" />
                        <h2 className="text-xs font-bold text-white uppercase tracking-widest">Borçlu Olunan Aylar</h2>
                    </div>
                    <span className="text-[10px] text-stone-600 font-bold">{borcluAylar.length} ay</span>
                </div>

                {borcluAylar.length === 0 ? (
                    <div className="p-8 text-center">
                        <CheckCircle2 size={48} className="text-green-500 mx-auto mb-3" />
                        <div className="text-stone-400 text-sm font-bold">Borçlu ayınız yok!</div>
                        <div className="text-stone-600 text-[10px] mt-1">Tüm aylar ödenmiş veya çalışma kaydı yok.</div>
                    </div>
                ) : (
                    <div className="divide-y divide-stone-800/50">
                        {borcluAylar.map(([ak, o]) => {
                            const kalan = o.hakedis - o.avans - o.odeme;
                            const isAcik = acikAylar.has(ak);

                            return (
                                <div key={ak}>
                                    <button
                                        onClick={() => toggleAy(ak)}
                                        className="w-full p-4 sm:p-5 flex items-center gap-4 hover:bg-stone-800/20 transition-all text-left"
                                    >
                                        <div className="w-12 h-12 rounded-2xl bg-red-500/10 border border-red-500/20 flex items-center justify-center flex-shrink-0">
                                            <span className="text-[10px] font-black text-red-400">{ak.split('-')[1]}</span>
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-white">{ak}</h3>
                                                <span className="text-[9px] font-black bg-stone-800 text-stone-500 px-2 py-0.5 rounded-full">
                                                    {o.gunSayisi} GÜN
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3 mt-1 text-[10px]">
                                                <span className="text-stone-600 font-bold">
                                                    H: <span className="text-green-400">{paraCevir(o.hakedis)}</span>
                                                </span>
                                                <span className="text-stone-600 font-bold">
                                                    A: <span className="text-yellow-500">-{paraCevir(o.avans)}</span>
                                                </span>
                                                {o.odeme > 0 && (
                                                    <span className="text-stone-600 font-bold">
                                                        Ö: <span className="text-blue-400">-{paraCevir(o.odeme)}</span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="text-right flex-shrink-0">
                                            <div className="text-lg font-black text-red-400">{paraCevir(kalan)}</div>
                                            <div className="text-[9px] text-stone-600 font-bold">ÖDENECEK</div>
                                        </div>

                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    odemeModalAc(ak);
                                                }}
                                                className="px-4 py-2 bg-red-500 text-white rounded-xl text-[10px] font-black hover:bg-red-400 transition-all active:scale-95"
                                            >
                                                ÖDE
                                            </button>
                                            {isAcik ? <ChevronUp size={18} className="text-stone-500" /> : <ChevronDown size={18} className="text-stone-500" />}
                                        </div>
                                    </button>

                                    {isAcik && (
                                        <div className="px-4 pb-4">
                                            <div className="bg-stone-800/30 rounded-2xl border border-stone-700/50 overflow-hidden">
                                                {/* Ay Özeti */}
                                                <div className="p-4 grid grid-cols-3 gap-4 border-b border-stone-700/50">
                                                    <div className="text-center">
                                                        <div className="text-[9px] text-stone-600 uppercase font-black mb-1">Hakediş</div>
                                                        <div className="text-sm font-black text-green-400">{paraCevir(o.hakedis)}</div>
                                                        <div className="text-[9px] text-stone-600">{o.gunSayisi} gün</div>
                                                    </div>
                                                    <div className="text-center border-x border-stone-700/30">
                                                        <div className="text-[9px] text-stone-600 uppercase font-black mb-1">Avans</div>
                                                        <div className="text-sm font-black text-yellow-500">-{paraCevir(o.avans)}</div>
                                                    </div>
                                                    <div className="text-center">
                                                        <div className="text-[9px] text-stone-600 uppercase font-black mb-1">Kalan</div>
                                                        <div className="text-sm font-black text-red-400">{paraCevir(kalan)}</div>
                                                    </div>
                                                </div>

                                                {/* Ödeme Kayıtları (Düzenle/Sil) */}
                                                {o.odemeKayitlari.length > 0 && (
                                                    <div className="px-4 py-3 border-b border-stone-700/50">
                                                        <div className="text-[10px] text-stone-600 uppercase font-black tracking-widest mb-2">Bu Ayın Ödemeleri</div>
                                                        {o.odemeKayitlari.map(odeme => {
                                                            const tarih = odeme.tarih?.toDate ? odeme.tarih.toDate() : new Date(odeme.tarih);
                                                            return (
                                                                <div key={odeme.id} className="flex items-center justify-between py-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <CheckCircle2 size={14} className="text-blue-400" />
                                                                        <span className="text-xs text-stone-400">{paraCevir(odeme.tutar)}</span>
                                                                        <span className="text-[9px] text-stone-600">{tarih.toLocaleDateString('tr-TR')}</span>
                                                                        {odeme.manuel && <span className="text-[8px] bg-stone-700 text-stone-400 px-1 rounded">Manuel</span>}
                                                                    </div>
                                                                    <div className="flex gap-1">
                                                                        <button
                                                                            onClick={() => odemeModalAc(ak, odeme)}
                                                                            className="p-1.5 text-stone-500 hover:text-white hover:bg-stone-700 rounded-lg transition-all"
                                                                        >
                                                                            <Edit3 size={12} />
                                                                        </button>
                                                                        <button
                                                                            onClick={() => odemeSil(odeme.id)}
                                                                            className="p-1.5 text-stone-500 hover:text-red-400 hover:bg-stone-700 rounded-lg transition-all"
                                                                        >
                                                                            <Trash2 size={12} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}

                                                {/* Butonlar */}
                                                <div className="p-4 space-y-2">
                                                    <button
                                                        onClick={() => odemeModalAc(ak)}
                                                        className="w-full py-3 bg-red-500 text-white rounded-xl text-sm font-black hover:bg-red-400 transition-all active:scale-95 flex items-center justify-center gap-2"
                                                    >
                                                        <CreditCard size={16} />
                                                        {paraCevir(kalan)} ÖDE
                                                    </button>

                                                    {/* Manuel ödendi işaretle */}
                                                    <button
                                                        onClick={() => odenmisIsaretle(ak)}
                                                        className="w-full py-2.5 bg-stone-800 text-stone-400 rounded-xl text-[10px] font-bold hover:bg-stone-700 hover:text-white transition-all flex items-center justify-center gap-2"
                                                    >
                                                        <Check size={14} />
                                                        ÖDENDİ İŞARETLE (Manuel Kapat)
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* ÖDENMİŞ AYLAR (Açılır-Kapanır Panel) */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {odenmisAylar.length > 0 && (
                <div className="bg-stone-900 border border-stone-800 rounded-3xl overflow-hidden shadow-2xl transition-all">
                    <button
                        onClick={() => setOdenmisAylariGoster(!odenmisAylariGoster)}
                        className="w-full px-6 py-4 border-b border-stone-800 bg-stone-900/50 flex items-center justify-between hover:bg-stone-800/30 transition-colors"
                    >
                        <div className="flex items-center gap-2">
                            <CheckCircle2 size={16} className="text-green-500" />
                            <h2 className="text-xs font-bold text-white uppercase tracking-widest">Ödenmiş Aylar</h2>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-[10px] text-stone-600 font-bold">{odenmisAylar.length} ay</span>
                            {odenmisAylariGoster ? (
                                <ChevronUp size={18} className="text-stone-500" />
                            ) : (
                                <ChevronDown size={18} className="text-stone-500" />
                            )}
                        </div>
                    </button>

                    {/* İçerik sadece göster komutu geldiğinde görünür */}
                    {odenmisAylariGoster && (
                        <div className="divide-y divide-stone-800/50 animate-in fade-in slide-in-from-top-2 duration-300">
                            {odenmisAylar.map(([ak, o]) => {
                                const isAcik = acikAylar.has(ak);
                                return (
                                    <div key={ak}>
                                        <button
                                            onClick={() => toggleAy(ak)}
                                            className="w-full p-4 sm:p-5 flex items-center gap-4 hover:bg-stone-800/20 transition-all text-left"
                                        >
                                            <div className="w-12 h-12 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center flex-shrink-0">
                                                <span className="text-[10px] font-black text-green-400">{ak.split('-')[1]}</span>
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="text-sm font-bold text-white">{ak}</h3>
                                                    <span className="text-[9px] font-black bg-green-500/10 text-green-400 px-2 py-0.5 rounded-full border border-green-500/20">
                                                        {o.gunSayisi} GÜN
                                                    </span>
                                                    <span className="text-[9px] font-black bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                                                        ÖDENMİŞ ✓
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 mt-1 text-[10px]">
                                                    <span className="text-stone-600 font-bold">
                                                        H: <span className="text-green-400">{paraCevir(o.hakedis)}</span>
                                                    </span>
                                                    <span className="text-stone-600 font-bold">
                                                        A: <span className="text-yellow-500">-{paraCevir(o.avans)}</span>
                                                    </span>
                                                    <span className="text-stone-600 font-bold">
                                                        Ö: <span className="text-blue-400">-{paraCevir(o.odeme)}</span>
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="text-right flex-shrink-0">
                                                <div className="text-lg font-black text-green-400">{paraCevir(o.hakedis - o.avans - o.odeme)}</div>
                                                <div className="text-[9px] text-stone-600 font-bold">KAPANDI</div>
                                            </div>

                                            {isAcik ? <ChevronUp size={18} className="text-stone-500" /> : <ChevronDown size={18} className="text-stone-500" />}
                                        </button>

                                        {isAcik && (
                                            <div className="px-4 pb-4">
                                                <div className="bg-stone-800/30 rounded-2xl border border-stone-700/50 overflow-hidden p-4">
                                                    <div className="text-[10px] text-stone-600 uppercase font-black tracking-widest mb-3">Ödeme Kayıtları</div>
                                                    {o.odemeKayitlari.map(odeme => {
                                                        const tarih = odeme.tarih?.toDate ? odeme.tarih.toDate() : new Date(odeme.tarih);
                                                        return (
                                                            <div key={odeme.id} className="flex items-center justify-between py-2">
                                                                <div className="flex items-center gap-2">
                                                                    <CheckCircle2 size={14} className="text-blue-400" />
                                                                    <span className="text-xs text-stone-400">{paraCevir(odeme.tutar)}</span>
                                                                    <span className="text-[9px] text-stone-600">{tarih.toLocaleDateString('tr-TR')}</span>
                                                                    {odeme.manuel && <span className="text-[8px] bg-stone-700 text-stone-400 px-1 rounded">Manuel</span>}
                                                                </div>
                                                                <div className="flex gap-1">
                                                                    <button onClick={() => odemeModalAc(ak, odeme)} className="p-1.5 text-stone-500 hover:text-white hover:bg-stone-700 rounded-lg transition-all">
                                                                        <Edit3 size={12} />
                                                                    </button>
                                                                    <button onClick={() => odemeSil(odeme.id)} className="p-1.5 text-stone-500 hover:text-red-400 hover:bg-stone-700 rounded-lg transition-all">
                                                                        <Trash2 size={12} />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {/* ═══════════════════════════════════════════════════════════ */}
            {/* TÜM DÖNEMLER ÖZETİ */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {tumAylar.length > 0 && (
                <div className="bg-stone-900/50 border border-stone-800 rounded-2xl p-4">
                    <h3 className="text-[10px] font-black text-stone-600 uppercase tracking-widest mb-3">Tüm Dönemler Özeti</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {tumAylar.map(([ak, o]) => {
                            const net = o.hakedis - o.avans - o.odeme;
                            const isBorclu = net > 0;
                            const hasWork = o.gunSayisi > 0;

                            if (!hasWork && o.avans === 0 && o.odeme === 0) return null;

                            return (
                                <div
                                    key={ak}
                                    onClick={() => toggleAy(ak)}
                                    className={`p-3 rounded-xl border cursor-pointer transition-all hover:scale-[1.02] ${isBorclu
                                            ? 'bg-red-500/5 border-red-500/20'
                                            : hasWork
                                                ? 'bg-green-500/5 border-green-500/20'
                                                : 'bg-stone-800/30 border-stone-700/30'
                                        }`}
                                >
                                    <div className="text-[10px] font-bold text-stone-500 mb-1">{ak}</div>
                                    <div className={`text-sm font-black ${isBorclu ? 'text-red-400' : 'text-green-400'}`}>
                                        {hasWork ? paraCevir(Math.abs(net)) : '-'}
                                    </div>
                                    <div className="text-[9px] text-stone-600 font-bold">
                                        {isBorclu ? 'BORÇLU' : hasWork ? 'ÖDENMİŞ' : 'BOŞ'}
                                    </div>
                                    {o.gunSayisi > 0 && (
                                        <div className="text-[8px] text-stone-700 mt-1">{o.gunSayisi} gün</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* ÖDEME GEÇMİŞİ (Tüm ödemeler) */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {odemeler.length > 0 && (
                <div className="bg-stone-900 border border-stone-800 rounded-3xl overflow-hidden shadow-2xl">
                    <button
                        onClick={() => setOdemeGecmisiAcik(!odemeGecmisiAcik)}
                        className="w-full px-6 py-4 border-b border-stone-800 bg-stone-900/50 flex items-center justify-between"
                    >
                        <div className="flex items-center gap-2">
                            <CreditCard size={16} className="text-stone-500" />
                            <h2 className="text-xs font-bold text-white uppercase tracking-widest">Tüm Ödeme Geçmişi</h2>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-stone-600 font-bold">{odemeler.length} kayıt</span>
                            {odemeGecmisiAcik ? <ChevronUp size={16} className="text-stone-500" /> : <ChevronDown size={16} className="text-stone-500" />}
                        </div>
                    </button>

                    {odemeGecmisiAcik && (
                        <div className="divide-y divide-stone-800/50">
                            {odemeler.map(odeme => {
                                const tarih = odeme.tarih?.toDate ? odeme.tarih.toDate() : new Date(odeme.tarih);
                                return (
                                    <div key={odeme.id} className="px-6 py-4 flex items-center justify-between hover:bg-stone-800/20 transition-all">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                                                <CreditCard size={16} className="text-blue-400" />
                                            </div>
                                            <div>
                                                <div className="text-sm font-bold text-white">{paraCevir(odeme.tutar)}</div>
                                                <div className="text-[10px] text-stone-600 font-bold">
                                                    {tarih.toLocaleDateString('tr-TR')} • {odeme.ilgiliAy || '-'}
                                                    {odeme.manuel && <span className="text-stone-500 ml-1">(Manuel)</span>}
                                                </div>
                                                {odeme.aciklama && (
                                                    <div className="text-[9px] text-stone-700">{odeme.aciklama}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => {
                                                    const ak = odeme.ilgiliAy || ayKey(tarih.getFullYear(), tarih.getMonth());
                                                    odemeModalAc(ak, odeme);
                                                }}
                                                className="p-2 text-stone-500 hover:text-white hover:bg-stone-700 rounded-lg transition-all"
                                            >
                                                <Edit3 size={14} />
                                            </button>
                                            <button
                                                onClick={() => odemeSil(odeme.id)}
                                                className="p-2 text-stone-500 hover:text-red-400 hover:bg-stone-700 rounded-lg transition-all"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════ */}
            {/* ÖDEME MODAL */}
            {/* ═══════════════════════════════════════════════════════════ */}
            {odemeModalAcik && odemeAy && (
                <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/95 backdrop-blur-md">
                    <div className="bg-stone-900 w-full max-w-md rounded-t-[2.5rem] sm:rounded-[2.5rem] max-h-[92vh] overflow-hidden flex flex-col border border-stone-800 shadow-2xl">
                        <div className="p-6 border-b border-stone-800 flex justify-between items-center bg-stone-900">
                            <div>
                                <h3 className="text-xl font-black text-white">
                                    {duzenlemeModu ? 'Ödemeyi Düzenle' : `${odemeAy} Ödemesi`}
                                </h3>
                                <p className="text-[10px] text-stone-500 font-black uppercase tracking-[0.2em]">
                                    {duzenlemeModu ? 'Kayıt Güncelleme' : 'Hesap Kesim'}
                                </p>
                            </div>
                            <button
                                onClick={modalKapat}
                                className="p-3 bg-stone-800 rounded-2xl text-stone-400 hover:text-white"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-6 space-y-6 overflow-y-auto">
                            {/* Ay Özeti */}
                            <div className="bg-stone-800/30 rounded-2xl p-5 border border-stone-700/50 space-y-3">
                                {(() => {
                                    const o = aylikOzet[odemeAy];
                                    if (!o) return null;
                                    const kalan = o.hakedis - o.avans - o.odeme;
                                    return (
                                        <>
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-stone-500 font-bold">Hakediş ({o.gunSayisi} gün):</span>
                                                <span className="text-green-400 font-black">{paraCevir(o.hakedis)}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-stone-500 font-bold">Avans:</span>
                                                <span className="text-yellow-500 font-black">-{paraCevir(o.avans)}</span>
                                            </div>
                                            <div className="flex justify-between items-center text-sm">
                                                <span className="text-stone-500 font-bold">Ödenen:</span>
                                                <span className="text-blue-400 font-black">-{paraCevir(o.odeme)}</span>
                                            </div>
                                            <div className="border-t-2 border-stone-700 pt-3 flex justify-between items-center">
                                                <span className="text-white font-bold text-sm">KALAN:</span>
                                                <span className="text-2xl font-black text-red-400">{paraCevir(Math.max(0, kalan))}</span>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Form */}
                            <div className="space-y-4">
                                <div>
                                    <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">
                                        Ödeme Tutarı (₺)
                                    </label>
                                    <input
                                        type="number"
                                        value={odemeTutari}
                                        onChange={e => setOdemeTutari(e.target.value)}
                                        className="w-full bg-stone-800 border-2 border-stone-700 rounded-2xl p-4 text-white text-center text-2xl font-black outline-none focus:border-green-500"
                                        placeholder="0"
                                        autoFocus
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Tarih</label>
                                        <input
                                            type="date"
                                            value={odemeTarihi}
                                            onChange={e => setOdemeTarihi(e.target.value)}
                                            className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-stone-600"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] text-stone-500 font-black uppercase tracking-widest mb-2 block">Açıklama</label>
                                        <input
                                            type="text"
                                            value={odemeAciklama}
                                            onChange={e => setOdemeAciklama(e.target.value)}
                                            className="w-full bg-stone-800 border border-stone-700 rounded-xl p-3 text-white text-sm font-bold outline-none focus:border-stone-600 placeholder-stone-600"
                                            placeholder="Örn: Mart kapatma"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* ÖDEME SONRASI TAHMİN */}
                            {Number(odemeTutari) > 0 && !duzenlemeModu && (() => {
                                const o = aylikOzet[odemeAy];
                                if (!o) return null;
                                const kalan = o.hakedis - o.avans - o.odeme;
                                const yeniKalan = Math.max(0, kalan - Number(odemeTutari));
                                return (
                                    <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-4">
                                        <div className="text-[10px] text-green-500 font-black uppercase tracking-widest mb-2">Ödeme Sonrası</div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-stone-500 text-xs font-bold">{odemeAy} yeni bakiye:</span>
                                            <span className="text-lg font-black text-green-400">{paraCevir(yeniKalan)}</span>
                                        </div>
                                        <div className="text-[9px] text-stone-600 mt-1">
                                            {yeniKalan === 0
                                                ? 'Bu ay tamamen kapanacak ✓'
                                                : `Hala ${paraCevir(yeniKalan)} borç kalacak`
                                            }
                                        </div>
                                    </div>
                                );
                            })()}

                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={modalKapat}
                                    className="py-4 rounded-2xl bg-stone-800 text-stone-400 font-bold text-xs hover:bg-stone-700 transition-all"
                                >
                                    İPTAL
                                </button>
                                <button
                                    onClick={odemeKaydet}
                                    disabled={!odemeTutari || Number(odemeTutari) <= 0}
                                    className="py-4 rounded-2xl bg-green-500 text-stone-900 font-black text-xs hover:bg-green-400 disabled:opacity-40 transition-all active:scale-95"
                                >
                                    {duzenlemeModu ? 'GÜNCELLE' : 'ÖDEME YAP & KES'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
